import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Car, Barcode, Loader2, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const VehicleFilter = ({ onFilter, className = "" }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("manual");
  
  // Manual selection state
  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [engines, setEngines] = useState([]);
  
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMake, setSelectedMake] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedEngine, setSelectedEngine] = useState("");

  // VIN state
  const [vin, setVin] = useState("");
  const [vinLoading, setVinLoading] = useState(false);
  const [vinVehicle, setVinVehicle] = useState(null);
  const [vinError, setVinError] = useState("");

  // Fetch years on mount
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await axios.get(`${API}/vehicles/years`);
        setYears(response.data);
      } catch (error) {
        console.error("Error fetching years:", error);
      }
    };
    fetchYears();
  }, []);

  // Fetch makes when year changes
  useEffect(() => {
    const fetchMakes = async () => {
      if (!selectedYear) {
        setMakes([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/vehicles/makes`, {
          params: { year: selectedYear }
        });
        setMakes(response.data);
      } catch (error) {
        console.error("Error fetching makes:", error);
      }
    };
    fetchMakes();
    setSelectedMake("");
    setSelectedModel("");
    setSelectedEngine("");
    setModels([]);
    setEngines([]);
  }, [selectedYear]);

  // Fetch models when make changes
  useEffect(() => {
    const fetchModels = async () => {
      if (!selectedMake) {
        setModels([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/vehicles/models`, {
          params: { make: selectedMake, year: selectedYear }
        });
        setModels(response.data);
      } catch (error) {
        console.error("Error fetching models:", error);
      }
    };
    fetchModels();
    setSelectedModel("");
    setSelectedEngine("");
    setEngines([]);
  }, [selectedMake, selectedYear]);

  // Fetch engines when model changes
  useEffect(() => {
    const fetchEngines = async () => {
      if (!selectedModel) {
        setEngines([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/vehicles/engines`, {
          params: { make: selectedMake, model: selectedModel, year: selectedYear }
        });
        setEngines(response.data);
      } catch (error) {
        console.error("Error fetching engines:", error);
      }
    };
    fetchEngines();
    setSelectedEngine("");
  }, [selectedModel, selectedMake, selectedYear]);

  const handleManualSearch = () => {
    const params = new URLSearchParams();
    if (selectedYear) params.set("year", selectedYear);
    if (selectedMake) params.set("make", selectedMake);
    if (selectedModel) params.set("model", selectedModel);
    if (selectedEngine) params.set("engine", selectedEngine);
    
    if (onFilter) {
      onFilter({
        year: selectedYear,
        make: selectedMake,
        model: selectedModel,
        engine: selectedEngine
      });
    } else {
      navigate(`/shop?${params.toString()}`);
    }
  };

  const handleClear = () => {
    setSelectedYear("");
    setSelectedMake("");
    setSelectedModel("");
    setSelectedEngine("");
    setVin("");
    setVinVehicle(null);
    setVinError("");
    if (onFilter) {
      onFilter({ year: "", make: "", model: "", engine: "" });
    }
  };

  const handleVINLookup = async (e) => {
    e.preventDefault();
    setVinError("");
    setVinVehicle(null);
    
    const cleanVin = vin.toUpperCase().trim();
    
    if (cleanVin.length !== 17) {
      setVinError("VIN must be exactly 17 characters");
      return;
    }
    
    if (/[IOQ]/.test(cleanVin)) {
      setVinError("VIN cannot contain letters I, O, or Q");
      return;
    }
    
    setVinLoading(true);
    try {
      const response = await axios.post(`${API}/vin/decode`, { vin: cleanVin });
      const data = response.data;
      
      if (!data.year || !data.make || !data.model) {
        setVinError("Could not decode VIN. Please verify it's correct.");
        return;
      }
      
      setVinVehicle(data);
      toast.success(`Found: ${data.year} ${data.make} ${data.model}`);
    } catch (err) {
      console.error("VIN lookup error:", err);
      setVinError(err.response?.data?.detail || "Failed to decode VIN");
    } finally {
      setVinLoading(false);
    }
  };

  const handleVINSearch = () => {
    if (!vinVehicle) return;
    
    const params = new URLSearchParams();
    params.set("year", vinVehicle.year);
    params.set("make", vinVehicle.make);
    params.set("model", vinVehicle.model);
    
    if (onFilter) {
      onFilter({
        year: vinVehicle.year.toString(),
        make: vinVehicle.make,
        model: vinVehicle.model,
        engine: vinVehicle.engine || ""
      });
    } else {
      navigate(`/shop?${params.toString()}`);
    }
  };

  return (
    <div className={`vehicle-filter-bar ${className}`} data-testid="vehicle-filter">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-2 mb-4 bg-gray-100 p-1">
          <TabsTrigger 
            value="manual" 
            className="data-[state=active]:bg-white data-[state=active]:text-primary font-medium"
          >
            <Car className="w-4 h-4 mr-2" />
            Select Vehicle
          </TabsTrigger>
          <TabsTrigger 
            value="vin" 
            className="data-[state=active]:bg-white data-[state=active]:text-primary font-medium"
          >
            <Barcode className="w-4 h-4 mr-2" />
            VIN Lookup
          </TabsTrigger>
        </TabsList>

        {/* Manual Selection Tab */}
        <TabsContent value="manual" className="mt-0">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1 w-full">
              {/* Year */}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">Year</label>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="year-select">
                    <SelectValue placeholder="Select Year" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()} className="text-gray-900">
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Make */}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">Make</label>
                <Select value={selectedMake} onValueChange={setSelectedMake} disabled={!selectedYear}>
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="make-select">
                    <SelectValue placeholder="Select Make" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 max-h-60">
                    {makes.map((make) => (
                      <SelectItem key={make} value={make} className="text-gray-900">
                        {make}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Model */}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">Model</label>
                <Select value={selectedModel} onValueChange={setSelectedModel} disabled={!selectedMake}>
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="model-select">
                    <SelectValue placeholder="Select Model" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 max-h-60">
                    {models.map((model) => (
                      <SelectItem key={model} value={model} className="text-gray-900">
                        {model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Engine */}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">Engine</label>
                <Select value={selectedEngine} onValueChange={setSelectedEngine} disabled={!selectedModel}>
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="engine-select">
                    <SelectValue placeholder="Select Engine" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 max-h-60">
                    {engines.map((engine) => (
                      <SelectItem key={engine} value={engine} className="text-gray-900">
                        {engine}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-2 w-full md:w-auto">
              <Button
                onClick={handleManualSearch}
                className="flex-1 md:flex-none bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider"
                data-testid="vehicle-search-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
              <Button
                onClick={handleClear}
                variant="outline"
                className="border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400"
                data-testid="vehicle-clear-btn"
              >
                Clear
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* VIN Lookup Tab */}
        <TabsContent value="vin" className="mt-0">
          <form onSubmit={handleVINLookup}>
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1 w-full">
                <label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">
                  Vehicle Identification Number (VIN)
                </label>
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Enter 17-character VIN (e.g., 1HGCM82633A004352)"
                    value={vin}
                    onChange={(e) => setVin(e.target.value.toUpperCase())}
                    maxLength={17}
                    className="bg-white border-gray-300 text-gray-900 uppercase tracking-wider font-mono pr-10"
                    data-testid="vin-input"
                  />
                  {vinVehicle && (
                    <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Find VIN on driver's dashboard or door jamb
                </p>
              </div>
              
              <div className="flex gap-2 w-full md:w-auto">
                <Button
                  type="submit"
                  disabled={vinLoading || vin.length !== 17}
                  className="flex-1 md:flex-none bg-secondary hover:bg-secondary/90 text-white font-bold uppercase tracking-wider"
                  data-testid="vin-decode-btn"
                >
                  {vinLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Decoding...
                    </>
                  ) : (
                    <>
                      <Barcode className="w-4 h-4 mr-2" />
                      Decode
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={handleClear}
                  variant="outline"
                  className="border-gray-300 text-gray-600"
                >
                  Clear
                </Button>
              </div>
            </div>
          </form>

          {/* VIN Error */}
          {vinError && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {vinError}
            </div>
          )}

          {/* VIN Result */}
          {vinVehicle && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="vin-result">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">Vehicle Found</span>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">
                    {vinVehicle.year} {vinVehicle.make} {vinVehicle.model}
                  </h4>
                  {vinVehicle.engine && (
                    <p className="text-gray-600 text-sm">Engine: {vinVehicle.engine}</p>
                  )}
                </div>
                <Button
                  onClick={handleVINSearch}
                  className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider"
                  data-testid="vin-search-parts-btn"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Find Compatible Parts
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default VehicleFilter;
